import React, { useState, useEffect } from 'react';
import {
  ChevronRight, ChevronLeft, X,
  Map, ShoppingBag,
  Users, User, Building2, Sparkles, Check,
  Briefcase, ArrowRight, Lock, Eye, EyeOff, ShieldCheck, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { PHONE_CODES } from './PhoneInput';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from './LanguageToggle';
import { isValidUrl, isValidPhoneDigits } from '../lib/validation';
import { checkPasswordRules, isPasswordValid, passwordRuleLabels } from '../lib/passwordPolicy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('cop31_token')}` } });

/* ─── Country flags ──────────────────────────────────────────── */
const COUNTRY_FLAGS = {
  'turkey': '🇹🇷', 'türkiye': '🇹🇷', 'germany': '🇩🇪', 'almanya': '🇩🇪',
  'belgium': '🇧🇪', 'belçika': '🇧🇪',
  'france': '🇫🇷', 'fransa': '🇫🇷', 'united states': '🇺🇸', 'usa': '🇺🇸',
  'united kingdom': '🇬🇧', 'uk': '🇬🇧', 'italy': '🇮🇹', 'italya': '🇮🇹',
  'spain': '🇪🇸', 'ispanya': '🇪🇸', 'japan': '🇯🇵', 'japonya': '🇯🇵',
  'china': '🇨🇳', 'çin': '🇨🇳', 'brazil': '🇧🇷', 'brezilya': '🇧🇷',
  'india': '🇮🇳', 'hindistan': '🇮🇳', 'canada': '🇨🇦', 'kanada': '🇨🇦',
  'australia': '🇦🇺', 'avustralya': '🇦🇺', 'netherlands': '🇳🇱', 'hollanda': '🇳🇱',
  'sweden': '🇸🇪', 'norway': '🇳🇴', 'norveç': '🇳🇴', 'denmark': '🇩🇰',
  'finland': '🇫🇮', 'switzerland': '🇨🇭', 'austria': '🇦🇹', 'avusturya': '🇦🇹',
  'south africa': '🇿🇦', 'kenya': '🇰🇪', 'egypt': '🇪🇬', 'mısır': '🇪🇬',
  'uae': '🇦🇪', 'saudi arabia': '🇸🇦', 'qatar': '🇶🇦', 'katar': '🇶🇦',
  'pakistan': '🇵🇰', 'indonesia': '🇮🇩', 'mexico': '🇲🇽', 'meksika': '🇲🇽',
  'argentina': '🇦🇷', 'chile': '🇨🇱', 'colombia': '🇨🇴', 'peru': '🇵🇪',
  'greece': '🇬🇷', 'yunanistan': '🇬🇷', 'portugal': '🇵🇹', 'portekiz': '🇵🇹',
  'poland': '🇵🇱', 'polonya': '🇵🇱', 'ukraine': '🇺🇦', 'ukrayna': '🇺🇦',
  'russia': '🇷🇺', 'rusya': '🇷🇺', 'south korea': '🇰🇷', 'güney kore': '🇰🇷',
  'thailand': '🇹🇭', 'vietnam': '🇻🇳', 'malaysia': '🇲🇾', 'singapore': '🇸🇬',
  'new zealand': '🇳🇿', 'nigeria': '🇳🇬', 'ghana': '🇬🇭', 'morocco': '🇲🇦',
  'fas': '🇲🇦', 'ethiopia': '🇪🇹', 'tanzania': '🇹🇿', 'israel': '🇮🇱',
  'iran': '🇮🇷', 'iraq': '🇮🇶', 'jordan': '🇯🇴', 'ürdün': '🇯🇴',
  'azerbaijan': '🇦🇿', 'azerbaycan': '🇦🇿',
};

const getFlag = (country) => {
  if (!country) return '🌍';
  const key = country.toLowerCase().trim();
  return COUNTRY_FLAGS[key] || '🌍';
};

/* ─── Country → Phone code lookup ───────────────────────────── */
const getPhoneCodeForCountry = (country) => {
  if (!country) return '+90';
  const key = country.toLowerCase().trim();
  const map = {
    'türkiye': '+90', 'turkey': '+90',
    'belçika': '+32', 'belgium': '+32',
    'germany': '+49', 'almanya': '+49',
    'france': '+33', 'fransa': '+33',
    'united states': '+1', 'usa': '+1', 'abd': '+1',
    'united kingdom': '+44', 'uk': '+44',
    'italy': '+39', 'italya': '+39',
    'spain': '+34', 'ispanya': '+34',
    'japan': '+81', 'japonya': '+81',
    'china': '+86', 'çin': '+86',
    'brazil': '+55', 'brezilya': '+55',
    'india': '+91', 'hindistan': '+91',
    'canada': '+1', 'kanada': '+1',
    'australia': '+61', 'avustralya': '+61',
    'netherlands': '+31', 'hollanda': '+31',
    'sweden': '+46', 'isveç': '+46',
    'norway': '+47', 'norveç': '+47',
    'denmark': '+45', 'danimarka': '+45',
    'finland': '+358', 'finlandiya': '+358',
    'switzerland': '+41', 'isviçre': '+41',
    'austria': '+43', 'avusturya': '+43',
    'south africa': '+27', 'güney afrika': '+27',
    'kenya': '+254',
    'egypt': '+20', 'mısır': '+20',
    'uae': '+971', 'birleşik arap emirlikleri': '+971',
    'saudi arabia': '+966', 'suudi arabistan': '+966',
    'qatar': '+974', 'katar': '+974',
    'pakistan': '+92',
    'indonesia': '+62', 'endonezya': '+62',
    'mexico': '+52', 'meksika': '+52',
    'argentina': '+54',
    'chile': '+56',
    'colombia': '+57',
    'peru': '+51',
    'greece': '+30', 'yunanistan': '+30',
    'portugal': '+351', 'portekiz': '+351',
    'poland': '+48', 'polonya': '+48',
    'ukraine': '+380', 'ukrayna': '+380',
    'russia': '+7', 'rusya': '+7',
    'south korea': '+82', 'güney kore': '+82',
    'thailand': '+66', 'tayland': '+66',
    'vietnam': '+84',
    'malaysia': '+60', 'malezya': '+60',
    'singapore': '+65',
    'new zealand': '+64', 'yeni zelanda': '+64',
    'nigeria': '+234',
    'ghana': '+233',
    'morocco': '+212', 'fas': '+212', 'maroko': '+212',
    'ethiopia': '+251', 'etiyopya': '+251',
    'tanzania': '+255',
    'israel': '+972', 'İsrail': '+972',
    'iran': '+98',
    'iraq': '+964', 'ırak': '+964',
    'jordan': '+962', 'ürdün': '+962',
    'azerbaijan': '+994', 'azerbaycan': '+994',
    'czech republic': '+420', 'çek cumhuriyeti': '+420',
    'hungary': '+36', 'macaristan': '+36',
    'romania': '+40', 'romanya': '+40',
    'bulgaria': '+359', 'bulgaristan': '+359',
    'croatia': '+385', 'hırvatistan': '+385',
    'serbia': '+381', 'sırbistan': '+381',
    'slovakia': '+421', 'slovakya': '+421',
    'kazakhstan': '+7', 'kazakistan': '+7',
    'philippines': '+63', 'filipinler': '+63',
    'bangladesh': '+880',
    'sri lanka': '+94',
    'myanmar': '+95',
    'cambodia': '+855', 'kamboçya': '+855',
    'algeria': '+213', 'cezayir': '+213',
    'tunisia': '+216', 'tunus': '+216',
    'senegal': '+221',
    'côte d\'ivoire': '+225',
    'cameroon': '+237', 'kamerun': '+237',
    'angola': '+244',
    'mozambique': '+258', 'mozambik': '+258',
    'zambia': '+260', 'zambiya': '+260',
    'zimbabwe': '+263',
    'ecuador': '+593',
    'venezuela': '+58',
    'bolivia': '+591',
    'paraguay': '+595',
    'uruguay': '+598',
    'costa rica': '+506',
    'panama': '+507',
    'cuba': '+53', 'küba': '+53',
    'dominican republic': '+1',
    'jamaica': '+1',
    'new guinea': '+675', 'papua yeni gine': '+675',
    'fiji': '+679',
    'iceland': '+354', 'İzlanda': '+354',
    'ireland': '+353', 'İrlanda': '+353',
    'luxembourg': '+352', 'lüksemburg': '+352',
    'malta': '+356',
    'cyprus': '+357', 'kıbrıs': '+357',
    'estonia': '+372', 'estonya': '+372',
    'latvia': '+371', 'letonya': '+371',
    'lithuania': '+370', 'litvanya': '+370',
    'slovenia': '+386', 'slovenya': '+386',
    'albania': '+355', 'arnavutluk': '+355',
    'north macedonia': '+389', 'kuzey makedonya': '+389',
    'bosnia': '+387', 'bosna': '+387',
    'moldova': '+373',
    'belarus': '+375', 'beyaz rusya': '+375',
    'georgia': '+995', 'gürcistan': '+995',
    'armenia': '+374', 'ermenistan': '+374',
    'uzbekistan': '+998', 'özbekistan': '+998',
    'kyrgyzstan': '+996', 'kırgızistan': '+996',
    'tajikistan': '+992', 'tacikistan': '+992',
    'turkmenistan': '+993',
    'mongolia': '+976', 'moğolistan': '+976',
    'nepal': '+977',
    'bhutan': '+975',
    'maldives': '+960', 'maldivler': '+960',
    // — Additional UNFCCC countries —
    'afghanistan': '+93', 'afganistan': '+93',
    'lebanon': '+961', 'lübnan': '+961',
    'syria': '+963', 'suriye': '+963',
    'yemen': '+967',
    'oman': '+968', 'umman': '+968',
    'bahrain': '+973', 'bahreyn': '+973',
    'kuwait': '+965', 'kuveyt': '+965',
    'libya': '+218', 'libya ': '+218',
    'sudan': '+249',
    'uganda': '+256',
    'rwanda': '+250', 'ruanda': '+250',
    'burundi': '+257',
    'madagascar': '+261', 'madagaskar': '+261',
    'namibia': '+264', 'namibya': '+264',
    'botswana': '+267',
    'mauritius': '+230',
    'seychelles': '+248',
    'barbados': '+1',
    'bahamas': '+1',
    'trinidad and tobago': '+1',
    'haiti': '+509',
    'guyana': '+592',
    'suriname': '+597',
    'liechtenstein': '+423',
    'monaco': '+377',
    'san marino': '+378',
    'andorra': '+376',
    'vatican': '+379',
  };
  return map[key] || '+90';
};

/* ─── Wizard steps definition ───────────────────────────────────
   Phase 86AY — `isGreenZone` skips the "Stand Tercihleri" step entirely.
   Green Zone admins assign stands directly (modular / space-only is decided
   at assignment time, not by the customer), so asking the customer to pick
   "Modüler vs Custom" during onboarding is confusing and unused. */
const buildSteps = (tr, needsPasswordStep = false, isGreenZone = false) => [
  {
    id: 'welcome',
    icon: Sparkles,
    titleTr: 'COP 31\'e Hoş Geldiniz!',
    titleEn: 'Welcome to COP 31!',
    isIntro: true,
  },
  // Şifre adımı — sadece UNFCCC kodu ile giriş yapan ve henüz şifre belirlemeyen kullanıcılar için
  ...(needsPasswordStep ? [{
    id: 'set_password',
    icon: ShieldCheck,
    titleTr: 'Güvenli Şifrenizi Belirleyin',
    titleEn: 'Set Your Secure Password',
    descTr: 'Bir sonraki girişte e-posta + şifre ile girebileceksiniz.',
    descEn: 'Next time you can log in with email and password.',
    isPasswordStep: true,
    fields: [
      { key: 'new_password',      labelTr: 'Yeni Şifre',        labelEn: 'New Password',      type: 'new_password',     placeholder: tr ? 'En az 8 karakter' : 'At least 8 characters' },
      { key: 'confirm_password',  labelTr: 'Şifreyi Tekrarla',  labelEn: 'Confirm Password',  type: 'confirm_password', placeholder: tr ? 'Şifreyi tekrar girin' : 'Re-enter password' },
    ],
  }] : []),
  {
    id: 'company',
    icon: Building2,
    titleTr: 'Firma Bilgileri',
    titleEn: 'Company Information',
    descTr: 'Firmanızla ilgili temel bilgileri girin.',
    descEn: 'Enter basic information about your company.',
    fields: [
      { key: 'organization_name', labelTr: 'Kurum / Organizasyon Adı', labelEn: 'Organization Name', type: 'text', placeholder: tr ? 'Örn: Çevre Bakanlığı' : 'E.g. Ministry of Environment' },
      { key: 'website', labelTr: 'Web Sitesi', labelEn: 'Website', type: 'url', placeholder: 'www.xxx.com' },
      { key: 'company_phone', labelTr: 'Sabit Telefon', labelEn: 'Landline', type: 'phone', placeholder: tr ? '5xx xxx xx xx' : '5xx xxx xx xx' },
    ],
  },
  {
    id: 'contact',
    icon: User,
    titleTr: 'İletişim Bilgileri',
    titleEn: 'Contact Details',
    descTr: 'Etkinlikte iletişim kurulacak kişi bilgilerini girin.',
    descEn: 'Enter the contact person details for the event.',
    fields: [
      { key: 'full_name', labelTr: 'Ad Soyad', labelEn: 'Full Name', type: 'text', placeholder: tr ? 'Adınız Soyadınız' : 'Your Full Name' },
      { key: 'title', labelTr: 'Ünvan / Pozisyon', labelEn: 'Title / Position', type: 'text', placeholder: tr ? 'Örn: Genel Müdür' : 'E.g. CEO' },
      { key: 'contact_phone', labelTr: 'Telefon', labelEn: 'Phone', type: 'phone', placeholder: tr ? '5xx xxx xx xx' : '5xx xxx xx xx' },
    ],
  },
  // Phase 86AY — Stand Tercihleri sadece Blue Zone için. Green Zone'da
  // stand atamasını ultra admin yapar, modular/space-only kararı atama
  // anında verilir, bu yüzden müşteriye sormak gereksiz/yanıltıcı.
  ...(isGreenZone ? [] : [{
    id: 'stand',
    icon: Map,
    titleTr: 'Stand Tercihleri',
    titleEn: 'Stand Preferences',
    descTr: 'Stand kurulumunuzla ilgili beklentilerinizi paylaşın.',
    descEn: 'Share your expectations about your stand setup.',
    fields: [
      {
        key: 'stand_design_type', labelTr: 'Stand Tasarım Türü', labelEn: 'Stand Design Type',
        type: 'toggle2',
        options: [
          { value: 'modular', labelTr: 'Modüler Hazır Stand', labelEn: 'Modular Ready Stand', descTr: 'Hızlı kurulum, uygun fiyat', descEn: 'Quick setup, cost-effective' },
          { value: 'custom', labelTr: 'Custom Design Stand', labelEn: 'Custom Design Stand', descTr: 'Özgün tasarım, marka kimliği', descEn: 'Unique design, brand identity' },
        ],
      },
      {
        key: 'stand_area', labelTr: 'Talep Edilen Alan (m²)', labelEn: 'Requested Area (m²)',
        type: 'area_smart', unit: 'm²', placeholder: tr ? 'Örn: 36' : 'E.g. 36',
      },
      {
        key: 'stand_shape', labelTr: 'Stand Tipi', labelEn: 'Stand Type',
        type: 'card_radio',
        options: [
          { value: 'island', labelTr: 'Ada', labelEn: 'Island', descTr: '4 açık kenar', descEn: '4 open sides' },
          { value: 'peninsula', labelTr: 'Yarımada', labelEn: 'Peninsula', descTr: '3 açık kenar', descEn: '3 open sides' },
          { value: 'linear', labelTr: 'Linear', labelEn: 'Linear', descTr: '1 açık kenar', descEn: '1 open side' },
          { value: 'corner', labelTr: 'Köşe', labelEn: 'Corner', descTr: '2 açık kenar', descEn: '2 open sides' },
        ],
      },
    ],
  }]),
  {
    id: 'team',
    icon: Users,
    titleTr: 'Ekip Kurulumu',
    titleEn: 'Team Setup',
    descTr: 'Ekip üyelerinizi daha sonra "Kullanıcılar" sekmesinden de davet edebilirsiniz.',
    descEn: 'You can also invite team members later from the "Users" tab.',
    fields: [
      {
        key: 'team_size', labelTr: 'Tahmini Ekip Büyüklüğü', labelEn: 'Estimated Team Size', type: 'select',
        options: [
          { value: '1-5', labelTr: '1-5 kişi', labelEn: '1-5 people' },
          { value: '5-10', labelTr: '5-10 kişi', labelEn: '5-10 people' },
          { value: '10-20', labelTr: '10-20 kişi', labelEn: '10-20 people' },
          { value: '20+', labelTr: '20+ kişi', labelEn: '20+ people' },
        ]
      },
    ],
  },
];

/* ─── Compact badge shown on form steps ─────────────────────── */
const ZoneBadge = ({ country, company, zone = 'blue_zone', tr }) => {
  const isGreen = zone === 'green_zone';
  return (
    <div className="flex items-center gap-2 mb-4 flex-wrap">
      <span className="bg-white/25 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full tracking-widest">COP 31</span>
      <span className="bg-white/15 text-white/80 text-[10px] font-semibold px-2.5 py-0.5 rounded-full tracking-wide">
        {isGreen ? 'GREEN ZONE' : 'BLUE ZONE'}
      </span>
      {isGreen ? (
        company && (
          <span className="flex items-center gap-1 bg-white/10 text-white text-[11px] font-medium px-2 py-0.5 rounded-full">
            <span>{company}</span>
          </span>
        )
      ) : (
        country && (
          <span className="flex items-center gap-1 bg-white/10 text-white text-[11px] font-medium px-2 py-0.5 rounded-full">
            <span>{getFlag(country)}</span>
            <span>{country}</span>
          </span>
        )
      )}
    </div>
  );
};

/* ─── Inline Phone Input ─────────────────────────────────────── */
const InlinePhoneInput = ({ codeKey, numberKey, form, set, tr, focusRing = 'focus:ring-blue-300', onBlur, hasError }) => {
  const handleNumber = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 15);
    set(numberKey, digits);
  };
  return (
    <div className="flex gap-2">
      <select
        value={form[codeKey] || '+90'}
        onChange={e => set(codeKey, e.target.value)}
        title={form[codeKey] || '+90'}
        className={`w-44 sm:w-52 border rounded-xl px-2 py-2.5 text-sm focus:outline-none focus:ring-2 ${focusRing} bg-white shrink-0 ${hasError ? 'border-red-300' : 'border-gray-200'}`}
      >
        {PHONE_CODES.map((c) => (
          <option key={c.code + c.label} value={c.code} title={c.label}>
            {c.country} {c.flag} ({c.code})
          </option>
        ))}
      </select>
      <input
        type="text"
        inputMode="numeric"
        value={form[numberKey] || ''}
        onChange={handleNumber}
        onBlur={() => onBlur && onBlur(form[numberKey])}
        maxLength={15}
        placeholder={tr ? '5xx xxx xx xx' : '5xx xxx xx xx'}
        className={`w-44 sm:w-56 border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${focusRing} ${hasError ? 'border-red-300' : 'border-gray-200'}`}
      />
    </div>
  );
};

/* ─── Resume key: hangi adımda kaldı? ───────────────────────── */
const getResumeKey = () => {
  try {
    const u = JSON.parse(localStorage.getItem('cop31_user') || '{}');
    // verify-code response'u user_id döndürür, id değil
    const uid = u.user_id || u.id || 'default';
    return `onboarding_wizard_step_${uid}`;
  } catch { return 'onboarding_wizard_step_default'; }
};

const getInitialStep = (maxStep) => {
  try {
    const saved = parseInt(localStorage.getItem(getResumeKey()) || '0', 10);
    return (saved >= 1 && saved <= maxStep) ? saved : 0;
  } catch { return 0; }
};

/* ─── Payload builder ────────────────────────────────────────── */
const buildPayload = (form) => ({
  ...form,
  company_phone: form.company_phone_code
    ? `${form.company_phone_code}${form.company_phone_number || ''}`
    : form.company_phone_number,
  contact_phone: form.contact_phone_code
    ? `${form.contact_phone_code}${form.contact_phone_number || ''}`
    : form.contact_phone_number,
  company_name: form.organization_name || form.company_name,
});

/* ─── Component ─────────────────────────────────────────────── */
const OnboardingWizard = ({ onClose, onSkip, userData = {} }) => {
  // Dil context'ten okunuyor — prop yerine doğrudan context
  const { language } = useLanguage();
  const tr = language === 'tr';

  // UNFCCC kodu ile giriş yapan kullanıcılar için şifre adımı
  const needsPassword = !userData.password_set;

  // API'den gelen ülke bilgisi — ZoneBadge ve intro screen için
  const [resolvedCountry, setResolvedCountry] = useState(userData.country || '');
  // Kullanıcı zone'u — intro/teması (blue_zone = ülke+bayrak, green_zone = şirket)
  const [resolvedZone, setResolvedZone] = useState(userData.zone || 'blue_zone');
  const [resolvedCompany, setResolvedCompany] = useState(userData.company || userData.company_name || '');
  const isGreenZone = resolvedZone === 'green_zone';

  // Phase 86AY — `isGreenZone` skips the Stand Tercihleri step; recompute on
  // every render so a late zone resolution (API) cleans up the step list.
  const steps = buildSteps(tr, needsPassword, isGreenZone);

  // Kaldığı adımdan başla (steps.length - 1 = son adım indeksi)
  const [stepIndex, setStepIndex] = useState(() => getInitialStep(steps.length - 1));
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  // Page-leave warning modal
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  // Inactivity timeout modal
  const [showIdleModal, setShowIdleModal] = useState(false);
  // Şifre alanları show/hide
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  // Theme tokens — same shape, different palette per zone
  const theme = isGreenZone
    ? {
        gradFrom:   'from-emerald-500',
        gradTo:     'to-teal-600',
        ring:       'shadow-emerald-200',
        accentText: 'text-emerald-600',
        accentTextLight: 'text-emerald-500',
        accentTextSoft:  'text-emerald-700',
        btnBg:      'bg-emerald-600 hover:bg-emerald-700',
        btnShadow:  'shadow-emerald-200',
        tileBg:     'bg-emerald-50',
        tileBorder: 'border-emerald-100',
        tileIconBg: 'bg-emerald-600',
        tileIconShadow: 'shadow-emerald-200',
        zoneLabel:  'GREEN ZONE',
        // — Form & step tokens —
        focusRing:       'focus:ring-emerald-300',
        doneIconBg:      'bg-emerald-100',
        doneIconText:    'text-emerald-600',
        topbarLogoBg:    'bg-emerald-600',
        stepDotActive:   'bg-emerald-600',
        stepDotPassed:   'bg-emerald-300',
        cardHeaderGrad:  'from-emerald-500 to-teal-600',
        cardSubText:     'text-emerald-100',
        infoBg:          'bg-emerald-50',
        infoBorder:      'border-emerald-200',
        infoText:        'text-emerald-700',
        infoIconText:    'text-emerald-500',
        selectedBorder:  'border-emerald-500',
        selectedBg:      'bg-emerald-50',
        selectedText:    'text-emerald-700',
        hoverBorderSoft: 'hover:border-emerald-200',
        chipActiveBg:    'bg-emerald-500',
        chipActiveShadow:'shadow-emerald-200',
        chipHover:       'hover:border-emerald-300 hover:text-emerald-600',
        checkboxActiveBg:'bg-emerald-500',
        primaryBtn:      'bg-emerald-600 hover:bg-emerald-700',
      }
    : {
        gradFrom:   'from-blue-500',
        gradTo:     'to-indigo-600',
        ring:       'shadow-blue-200',
        accentText: 'text-blue-600',
        accentTextLight: 'text-blue-500',
        accentTextSoft:  'text-blue-700',
        btnBg:      'bg-blue-600 hover:bg-blue-700',
        btnShadow:  'shadow-blue-200',
        tileBg:     'bg-blue-50',
        tileBorder: 'border-blue-100',
        tileIconBg: 'bg-blue-600',
        tileIconShadow: 'shadow-blue-200',
        zoneLabel:  'BLUE ZONE',
        // — Form & step tokens —
        focusRing:       'focus:ring-blue-300',
        doneIconBg:      'bg-blue-100',
        doneIconText:    'text-blue-600',
        topbarLogoBg:    'bg-blue-600',
        stepDotActive:   'bg-blue-600',
        stepDotPassed:   'bg-blue-300',
        cardHeaderGrad:  'from-blue-500 to-indigo-600',
        cardSubText:     'text-blue-200',
        infoBg:          'bg-blue-50',
        infoBorder:      'border-blue-200',
        infoText:        'text-blue-700',
        infoIconText:    'text-blue-500',
        selectedBorder:  'border-blue-500',
        selectedBg:      'bg-blue-50',
        selectedText:    'text-blue-700',
        hoverBorderSoft: 'hover:border-blue-200',
        chipActiveBg:    'bg-blue-500',
        chipActiveShadow:'shadow-blue-200',
        chipHover:       'hover:border-blue-300 hover:text-blue-600',
        checkboxActiveBg:'bg-blue-500',
        primaryBtn:      'bg-blue-600 hover:bg-blue-700',
      };
  // Modüler stand alan seçenekleri (backend'den çekilir, varsayılan: 25/50/100/150)
  const [modularAreas, setModularAreas] = useState([25, 50, 100, 150]);

  // Modüler alan seçeneklerini backend'den çek
  useEffect(() => {
    axios.get(`${API}/ultra-admin/stand-options`)
      .then(r => setModularAreas(r.data?.modular_areas || [25, 50, 100, 150]))
      .catch(() => {});
  }, []);

  // Pre-fill all fields from API on mount
  useEffect(() => {
    const fetchAndPrefill = async () => {
      const prefill = {};
      // Set phone code based on user's country (default before API response)
      const defaultCode = getPhoneCodeForCountry(userData.country);
      prefill.company_phone_code = defaultCode;
      prefill.contact_phone_code = defaultCode;

      if (userData.full_name) prefill.full_name = userData.full_name;

      try {
        const res = await axios.get(`${API}/customer/profile`, getAuth());
        const d = res.data || {};
        // organization_name geliyorsa direkt, yoksa company_name'i organization_name'e map et
        const orgName = d.organization_name || d.company_name || d.company;
        if (orgName) prefill.organization_name = orgName;
        if (d.full_name) prefill.full_name = d.full_name;
        // Title / Pozisyon
        if (d.title || d.position) prefill.title = d.title || d.position;
        if (d.website) prefill.website = d.website;
        // Phone: profile sadece tek "phone" döndürüyor — hem company hem contact alanına yansıt.
        // Phone string "+90 5xx..." gibi gelebilir → kod + numara olarak ayır.
        const parsePhone = (raw) => {
          const s = String(raw || '').trim();
          if (!s) return { code: null, number: '' };
          const m = s.match(/^(\+\d{1,4})[\s-]*(.*)$/);
          if (m) {
            return { code: m[1], number: m[2].replace(/\D/g, '').slice(0, 15) };
          }
          return { code: null, number: s.replace(/\D/g, '').slice(0, 15) };
        };
        if (d.contact_phone_number) {
          prefill.contact_phone_number = d.contact_phone_number;
        } else if (d.phone) {
          const { code, number } = parsePhone(d.phone);
          if (number) prefill.contact_phone_number = number;
          if (code) prefill.contact_phone_code = code;
        }
        if (d.company_phone_number) {
          prefill.company_phone_number = d.company_phone_number;
        } else if (d.phone) {
          const { code, number } = parsePhone(d.phone);
          if (number) prefill.company_phone_number = number;
          if (code) prefill.company_phone_code = code;
        }
        // Override phone code ve ülke bilgisini API'den al
        if (d.country) {
          const code = getPhoneCodeForCountry(d.country);
          // Sadece phone halen yoksa varsayılan kodu yaz (yukarıdaki kod öncelikli)
          if (!prefill.company_phone_code) prefill.company_phone_code = code;
          if (!prefill.contact_phone_code) prefill.contact_phone_code = code;
          setResolvedCountry(d.country);
        }
        // Zone + company — ensure Green Zone users see the right palette & company name
        if (d.zone) setResolvedZone(d.zone);
        if (d.company || d.company_name || d.organization_name) {
          setResolvedCompany(d.company || d.company_name || d.organization_name);
        }
      } catch { /* fallback to userData */ }

      if (Object.keys(prefill).length > 0) {
        setForm(f => ({ ...f, ...prefill }));
      }
    };

    fetchAndPrefill();
  // eslint-disable-next-line
  }, []);

  // ── beforeunload warning (browser close / refresh / navigate away) ──
  useEffect(() => {
    if (done || stepIndex === 0) return; // no warning on intro or after completion
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = ''; // Chrome requires returnValue to be set
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [done, stepIndex]);

  // ── Inactivity timeout (10 minutes) ──
  useEffect(() => {
    if (done || stepIndex === 0) return;
    const IDLE_MS = 10 * 60 * 1000; // 10 minutes
    let timer = null;
    const reset = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setShowIdleModal(true), IDLE_MS);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(ev => window.addEventListener(ev, reset, { passive: true }));
    reset();
    return () => {
      if (timer) clearTimeout(timer);
      events.forEach(ev => window.removeEventListener(ev, reset));
    };
  }, [done, stepIndex]);

  const current = steps[stepIndex];
  const isIntro = current.isIntro;
  const isLast = stepIndex === steps.length - 1;
  const progress = Math.round((stepIndex / (steps.length - 1)) * 100);

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    // Hata varsa alanı düzeltince temizle
    setFormErrors(e => { const n = { ...e }; delete n[key]; return n; });
  };

  const goNext = async () => {
    // Intro adımı — sadece ilerle, kayıt yok
    if (isIntro) {
      setStepIndex(1);
      return;
    }

    // ── Zorunlu alan kontrolü ────────────────────────────────────────────────
    const errors = validateCurrentStep();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // İlk hatalı alana scroll
      const firstErrKey = Object.keys(errors)[0];
      const el = document.getElementById(`wizard-field-${firstErrKey}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setFormErrors({});

    setSaving(true);

    // ── Şifre belirleme adımı ────────────────────────────────────────────────
    if (current.id === 'set_password') {
      try {
        await axios.put(`${API}/customer/set-password`, { password: form.new_password }, getAuth());
        // localStorage'daki kullanıcı verisini güncelle — bir daha şifre adımı gösterme
        try {
          const ud = JSON.parse(localStorage.getItem('cop31_user') || '{}');
          ud.password_set = true;
          localStorage.setItem('cop31_user', JSON.stringify(ud));
        } catch { /* ignore */ }
      } catch {
        setFormErrors({ new_password: tr ? 'Şifre kaydedilemedi, tekrar deneyin.' : 'Could not save password, try again.' });
        setSaving(false);
        return;
      }
      localStorage.setItem(getResumeKey(), String(stepIndex + 1));
      setSaving(false);
      setStepIndex(i => i + 1);
      return;
    }

    // ── Her adımda kısmi kayıt ──────────────────────────────────────
    // Hata olsa bile ilerle (veri kaybetme, UI bloklanmasın)
    try {
      const payload = buildPayload(form);
      await axios.put(`${API}/customer/profile`, payload, getAuth());
    } catch { /* kısmi kayıt başarısız — devam et */ }

    if (isLast) {
      // Son adım: onboarding tamamlandı → tenant aktif et + hoş geldiniz maili
      try {
        await axios.post(`${API}/customer/onboarding/finish`, {}, getAuth());
        localStorage.removeItem(getResumeKey()); // Tamamlandı — artık resume gerekmiyor
      } catch { /* ignore */ }
      setSaving(false);
      setDone(true);
      setTimeout(() => onClose(), 1800);
      return;
    }

    // Bir sonraki adımı localStorage'a kaydet (resume için)
    localStorage.setItem(getResumeKey(), String(stepIndex + 1));
    setSaving(false);
    setStepIndex(i => i + 1);
  };

  const goBack = () => setStepIndex(i => i - 1);

  // ── Adım bazlı zorunlu alan kontrolü ─────────────────────────────────────
  // Her field key → hangi form key'i kontrol edilmeli
  const STEP_REQUIRED = {
    set_password: [
      { key: 'new_password',     phoneKey: null },
      { key: 'confirm_password', phoneKey: null },
    ],
    company: [
      { key: 'organization_name', phoneKey: null },
      { key: 'website', phoneKey: null, type: 'url' },
      { key: 'company_phone_number', phoneKey: 'company_phone_number' }, // phone type → numberKey
    ],
    contact: [
      { key: 'full_name', phoneKey: null },
      { key: 'title', phoneKey: null },
      { key: 'contact_phone_number', phoneKey: 'contact_phone_number' },
    ],
    stand: [
      { key: 'stand_design_type', phoneKey: null },
      { key: 'stand_area', phoneKey: null },
      { key: 'stand_shape', phoneKey: null },
    ],
    team: [
      { key: 'team_size', phoneKey: null },
    ],
  };

  const validateCurrentStep = () => {
    const required = STEP_REQUIRED[current?.id] || [];
    const errors = {};
    const errorMsg = tr ? 'Bu alan zorunludur.' : 'This field is required.';
    const arrayMsg = tr ? 'En az bir seçenek seçin.' : 'Select at least one option.';
    const urlErrorMsg = tr
      ? 'Geçersiz web adresi. Örnek: www.xxx.com'
      : 'Invalid website. Example: www.xxx.com';
    const phoneErrorMsg = tr
      ? 'Telefon 7–15 rakam içermelidir.'
      : 'Phone must be 7–15 digits.';

    // Şifre adımı için özel validasyon (genel kural: 8+ karakter, büyük, küçük, rakam, özel)
    if (current?.id === 'set_password') {
      const pwd = (form.new_password || '').trim();
      const conf = (form.confirm_password || '').trim();
      if (!pwd) {
        errors.new_password = errorMsg;
      } else if (!isPasswordValid(pwd)) {
        const r = checkPasswordRules(pwd);
        const missing = [];
        if (!r.length)    missing.push(tr ? 'en az 8 karakter' : 'min 8 chars');
        if (!r.uppercase) missing.push(tr ? 'büyük harf'       : 'uppercase');
        if (!r.lowercase) missing.push(tr ? 'küçük harf'       : 'lowercase');
        if (!r.number)    missing.push(tr ? 'rakam'            : 'number');
        if (!r.special)   missing.push(tr ? 'özel karakter'    : 'special char');
        errors.new_password = (tr ? 'Eksik: ' : 'Missing: ') + missing.join(', ');
      }
      if (!conf) {
        errors.confirm_password = errorMsg;
      } else if (pwd && conf !== pwd) {
        errors.confirm_password = tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.';
      }
      return errors;
    }

    for (const field of required) {
      const val = form[field.key];
      if (field.isArray) {
        if (!val || (Array.isArray(val) && val.length === 0)) {
          errors[field.key] = arrayMsg;
        }
      } else if (val === undefined || val === null || String(val).trim() === '') {
        errors[field.key] = errorMsg;
      } else if (field.type === 'url' && !isValidUrl(String(val).trim())) {
        errors[field.key] = urlErrorMsg;
      } else if (field.key.endsWith('phone_number') && !isValidPhoneDigits(String(val).trim(), { min: 7, max: 15 })) {
        errors[field.key] = phoneErrorMsg;
      }
    }
    return errors;
  };

  // Skip: mevcut adımı kaydet → bir sonraki açılışta buradan devam
  const handleSkip = () => {
    if (!isIntro) {
      localStorage.setItem(getResumeKey(), String(stepIndex));
    }
    if (onSkip) onSkip();
  };

  // Kullanıcı "X" veya sekmeyi kapatma eylemine benzer bir şey tetiklediğinde
  const requestClose = () => {
    if (stepIndex === 0) {
      // Intro: direkt skip
      handleSkip();
    } else {
      setShowLeaveModal(true);
    }
  };

  const confirmLeave = () => {
    setShowLeaveModal(false);
    // İlerlemeyi kaybet — resume key'i sil
    try { localStorage.removeItem(getResumeKey()); } catch { /* ignore */ }
    handleSkip();
  };

  const handleIdleTimeout = () => {
    // Oturum zaman aşımı — ilerlemeyi sıfırla ve modal kapatıldığında onSkip çalıştır
    setShowIdleModal(false);
    try { localStorage.removeItem(getResumeKey()); } catch { /* ignore */ }
    handleSkip();
  };

  if (done) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className={`h-20 w-20 rounded-full ${theme.doneIconBg} flex items-center justify-center mx-auto`}>
            <Check className={`h-10 w-10 ${theme.doneIconText}`} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">{tr ? 'Harika! Hazırsınız.' : 'Great! You\'re all set.'}</h2>
          <p className="text-gray-400 text-sm">{tr ? 'Panelinize yönlendiriliyorsunuz...' : 'Redirecting to your dashboard...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-gray-50 flex flex-col">

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 bg-white">
        <div className="flex items-center gap-3">
          <div className={`h-8 w-8 rounded-lg ${theme.topbarLogoBg} flex items-center justify-center`}>
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <span className="font-playfair text-lg text-[#0A3A5A] tracking-tight">COP 31 · Antalya</span>
        </div>
        {/* Step dots */}
        {!isIntro && (
          <div className="flex items-center gap-1.5">
            {steps.filter(s => !s.isIntro).map((s, i) => {
              const idx = i + 1;
              const active = stepIndex === idx;
              const passed = stepIndex > idx;
              return (
                <div key={s.id} className={`transition-all rounded-full ${
                  active ? `h-2 w-6 ${theme.stepDotActive}` :
                  passed ? `h-2 w-2 ${theme.stepDotPassed}` :
                  'h-2 w-2 bg-gray-200'
                }`} />
              );
            })}
          </div>
        )}
        {/* Sağ taraf: dil toggle + close */}
        <div className="flex items-center gap-3">
          <LanguageToggle variant="admin" />
          {stepIndex > 0 && (
            <button
              type="button"
              onClick={requestClose}
              data-testid="wizard-close-btn"
              className="h-8 w-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label={tr ? 'Kapat' : 'Close'}
              title={tr ? 'Kapat' : 'Close'}
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-lg">

          {/* ── Intro screen ── */}
          {isIntro ? (
            <div className="text-center space-y-6">
              {/* COP31 box + Country flag (Blue Zone) / Company badge (Green Zone) */}
              <div className="flex items-center justify-center gap-4">
                <div className={`h-24 w-24 rounded-3xl bg-gradient-to-br ${theme.gradFrom} ${theme.gradTo} flex flex-col items-center justify-center shadow-2xl ${theme.ring} flex-shrink-0`}>
                  <span className="text-white font-black text-xl leading-none tracking-tight">COP</span>
                  <span className="text-white font-black text-3xl leading-none tracking-tighter">31</span>
                  <div className="mt-1.5 bg-white/20 text-white text-[8px] font-bold px-2 py-0.5 rounded-full tracking-widest">{theme.zoneLabel}</div>
                </div>
                {isGreenZone ? (
                  resolvedCompany && (
                    <div className="h-24 flex flex-col items-center justify-center gap-1.5 max-w-[160px]">
                      <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-200">
                        <span className="text-white text-2xl font-black">{(resolvedCompany || '?').charAt(0).toUpperCase()}</span>
                      </div>
                      <span className="text-xs font-bold text-gray-700 bg-white/80 px-3 py-0.5 rounded-full border border-gray-100 shadow-sm truncate max-w-full" title={resolvedCompany}>{resolvedCompany}</span>
                    </div>
                  )
                ) : (
                  resolvedCountry && (
                    <div className="h-24 flex flex-col items-center justify-center gap-1.5">
                      <span className="text-[64px] leading-none drop-shadow-md">{getFlag(resolvedCountry)}</span>
                      <span className="text-xs font-bold text-gray-700 bg-white/80 px-3 py-0.5 rounded-full border border-gray-100 shadow-sm">{resolvedCountry}</span>
                    </div>
                  )
                )}
              </div>

              {/* Welcome text */}
              <div className="space-y-1">
                <p className={`${theme.accentTextLight} text-xs font-bold tracking-widest uppercase`}>COP 31 Antalya 2026</p>
                <h1 className="text-3xl font-black text-gray-900 leading-tight">
                  {tr ? 'Hoş Geldiniz' : 'Welcome'},<br />
                  <span className={theme.accentText}>
                    {form.full_name?.split(' ')[0] || userData.full_name?.split(' ')[0] || (tr ? 'Değerli Katılımcı' : 'Participant')}
                  </span>
                  {(isGreenZone ? resolvedCompany : resolvedCountry) && (
                    <span className="text-gray-400 font-medium text-xl ml-2">· {isGreenZone ? resolvedCompany : resolvedCountry}</span>
                  )}
                </h1>
              </div>

              <p className="text-gray-500 text-sm leading-relaxed max-w-sm mx-auto">
                {tr
                  ? 'Profilinizi tamamlayarak stand düzeninize erişin, tedarikçi ürünlerini inceleyin ve ekibinizi davet edin.'
                  : 'Complete your profile to access your stand layout, explore supplier products and invite your team.'}
              </p>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Map,         labelTr: 'Stand\nDüzeniniz',  labelEn: 'Your Stand\nLayout' },
                  { icon: ShoppingBag, labelTr: 'Ürün\nKataloğu',   labelEn: 'Product\nCatalog' },
                  { icon: Users,       labelTr: 'Ekip\nDavetiyesi', labelEn: 'Team\nInvite' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className={`${theme.tileBg} rounded-2xl p-3 border ${theme.tileBorder} text-center`}>
                      <div className={`h-9 w-9 rounded-xl ${theme.tileIconBg} flex items-center justify-center mx-auto mb-2 shadow-md ${theme.tileIconShadow}`}>
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                      <p className={`text-xs ${theme.accentTextSoft} font-semibold leading-snug whitespace-pre-line`}>{tr ? item.labelTr : item.labelEn}</p>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={goNext}
                className={`w-full ${theme.btnBg} text-white font-bold py-3.5 rounded-2xl text-base flex items-center justify-center gap-2 transition-all shadow-lg ${theme.btnShadow}`}
              >
                {tr ? 'Profili Tamamla' : 'Complete My Profile'}
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>

          ) : (
            /* ── Step form ── */
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Card header */}
              <div className={`bg-gradient-to-r ${theme.cardHeaderGrad} px-8 py-5`}>
                {/* Zone + Country compact badge */}
                <ZoneBadge country={resolvedCountry} company={resolvedCompany} zone={resolvedZone} tr={tr} />
                <div className="flex items-center gap-3">
                  {React.createElement(current.icon, { className: 'h-5 w-5 text-white' })}
                  <div>
                    <h2 className="text-lg font-bold text-white">{tr ? current.titleTr : current.titleEn}</h2>
                    <p className={`${theme.cardSubText} text-xs mt-0.5`}>{tr ? current.descTr : current.descEn}</p>
                  </div>
                </div>
                {/* Progress bar + step counter */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-white/70 text-[11px] font-semibold">
                      {tr ? `Adım ${stepIndex}/${steps.length - 1}` : `Step ${stepIndex}/${steps.length - 1}`}
                    </span>
                    <span className="text-white font-bold text-[11px]">{progress}%</span>
                  </div>
                  <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              </div>

              {/* Fields */}
              <div className="px-8 py-5 space-y-4">
                {/* Şifre adımı için bilgi kutusu */}
                {current.id === 'set_password' && (
                  <div className={`flex items-start gap-3 ${theme.infoBg} border ${theme.infoBorder} rounded-xl p-3.5 mb-1`}>
                    <Lock className={`h-4 w-4 ${theme.infoIconText} mt-0.5 shrink-0`} />
                    <p className={`text-xs ${theme.infoText} leading-relaxed`}>
                      {tr
                        ? 'UNFCCC kodu ile giriş yaptınız. Kendinize özel bir şifre belirleyin; bundan sonra şifrenizle de giriş yapabilirsiniz.'
                        : 'You logged in via UNFCCC code. Set a personal password to log in with email + password in the future.'}
                    </p>
                  </div>
                )}
                {(current.fields || []).map(field => {
                  // Determine phone code/number keys based on field key
                  const isCompanyPhone = field.key === 'company_phone';
                  const isContactPhone = field.key === 'contact_phone';
                  const codeKey = isCompanyPhone ? 'company_phone_code' : 'contact_phone_code';
                  const numberKey = isCompanyPhone ? 'company_phone_number' : 'contact_phone_number';

                  return (
                    <div key={field.key} id={`wizard-field-${isCompanyPhone ? 'company_phone_number' : isContactPhone ? 'contact_phone_number' : field.key}`}>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {tr ? field.labelTr : field.labelEn}
                        <span className="text-red-400 ml-1">*</span>
                      </label>
                      {/* Hata mesajı */}
                      {formErrors[isCompanyPhone ? 'company_phone_number' : isContactPhone ? 'contact_phone_number' : field.key] && (
                        <p className="text-xs text-red-500 mb-1.5 flex items-center gap-1">
                          <span>⚠</span>
                          {formErrors[isCompanyPhone ? 'company_phone_number' : isContactPhone ? 'contact_phone_number' : field.key]}
                        </p>
                      )}
                      {field.type === 'phone' ? (
                        <div className={formErrors[isCompanyPhone ? 'company_phone_number' : 'contact_phone_number'] ? 'ring-2 ring-red-300 rounded-xl' : ''}>
                        <InlinePhoneInput
                          codeKey={codeKey}
                          numberKey={numberKey}
                          form={form}
                          set={(k, v) => { set(k, v); setFormErrors(e => { const n={...e}; delete n[isCompanyPhone ? 'company_phone_number' : 'contact_phone_number']; return n; }); }}
                          tr={tr}
                          focusRing={theme.focusRing}
                          hasError={!!formErrors[isCompanyPhone ? 'company_phone_number' : 'contact_phone_number']}
                          onBlur={(val) => {
                            const key = isCompanyPhone ? 'company_phone_number' : 'contact_phone_number';
                            const v = String(val || '').trim();
                            if (!v) return; // empty → do not show error on blur (submit flow will catch)
                            if (!isValidPhoneDigits(v, { min: 7, max: 15 })) {
                              setFormErrors(prev => ({
                                ...prev,
                                [key]: tr ? 'Telefon 7–15 rakam içermelidir.' : 'Phone must be 7–15 digits.'
                              }));
                            } else {
                              setFormErrors(prev => { const n = {...prev}; delete n[key]; return n; });
                            }
                          }}
                        />
                        </div>
                      ) : field.type === 'new_password' || field.type === 'confirm_password' ? (
                        /* ── Password input with show/hide toggle ── */
                        <>
                          <div className="relative">
                            <input
                              type={(field.type === 'new_password' ? showNewPwd : showConfirmPwd) ? 'text' : 'password'}
                              value={form[field.key] || ''}
                              onChange={e => set(field.key, e.target.value)}
                              onBlur={() => {
                                const val = String(form[field.key] || '').trim();
                                if (field.type === 'new_password') {
                                  // On blur: if value entered but invalid → show error
                                  if (val && !isPasswordValid(val)) {
                                    const r = checkPasswordRules(val);
                                    const missing = [];
                                    if (!r.length)    missing.push(tr ? 'en az 8 karakter' : 'min 8 chars');
                                    if (!r.uppercase) missing.push(tr ? 'büyük harf'       : 'uppercase');
                                    if (!r.lowercase) missing.push(tr ? 'küçük harf'       : 'lowercase');
                                    if (!r.number)    missing.push(tr ? 'rakam'            : 'number');
                                    if (!r.special)   missing.push(tr ? 'özel karakter'    : 'special char');
                                    setFormErrors(prev => ({
                                      ...prev,
                                      new_password: (tr ? 'Eksik: ' : 'Missing: ') + missing.join(', ')
                                    }));
                                  }
                                  // Also re-validate confirm on new_password blur
                                  const conf = String(form.confirm_password || '').trim();
                                  if (conf && val && conf !== val) {
                                    setFormErrors(prev => ({
                                      ...prev,
                                      confirm_password: tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.'
                                    }));
                                  } else if (conf && val && conf === val) {
                                    setFormErrors(prev => { const n = {...prev}; delete n.confirm_password; return n; });
                                  }
                                } else if (field.type === 'confirm_password') {
                                  const pwd = String(form.new_password || '').trim();
                                  if (val && pwd && val !== pwd) {
                                    setFormErrors(prev => ({
                                      ...prev,
                                      confirm_password: tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.'
                                    }));
                                  } else if (val && pwd && val === pwd) {
                                    setFormErrors(prev => { const n = {...prev}; delete n.confirm_password; return n; });
                                  }
                                }
                              }}
                              placeholder={field.placeholder}
                              autoComplete={field.type === 'new_password' ? 'new-password' : 'new-password'}
                              data-testid={`wizard-${field.type.replace('_', '-')}-input`}
                              className={`w-full border rounded-xl px-4 py-2.5 pr-11 text-sm focus:outline-none focus:ring-2 ${formErrors[field.key] ? 'border-red-300 focus:ring-red-200' : `border-gray-200 ${theme.focusRing}`}`}
                            />
                            <button
                              type="button"
                              onClick={() => field.type === 'new_password' ? setShowNewPwd(v => !v) : setShowConfirmPwd(v => !v)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                              tabIndex={-1}
                            >
                              {(field.type === 'new_password' ? showNewPwd : showConfirmPwd)
                                ? <EyeOff className="h-4 w-4" />
                                : <Eye className="h-4 w-4" />
                              }
                            </button>
                          </div>
                          {field.type === 'new_password' && (form[field.key] || '').length > 0 && (() => {
                            const rules = checkPasswordRules(form[field.key]);
                            const labels = passwordRuleLabels(tr);
                            return (
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                                {['length','uppercase','lowercase','number','special'].map(k => (
                                  <div key={k} className={`flex items-center gap-1.5 text-xs ${rules[k] ? 'text-emerald-600' : 'text-gray-400'}`}>
                                    {rules[k]
                                      ? <CheckCircle className="h-3 w-3" />
                                      : <div className="h-3 w-3 rounded-full border border-gray-300" />}
                                    <span>{labels[k]}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          value={form[field.key] || ''}
                          onChange={e => set(field.key, e.target.value)}
                          placeholder={field.placeholder}
                          rows={3}
                          className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 resize-none ${formErrors[field.key] ? 'border-red-300 focus:ring-red-200' : `border-gray-200 ${theme.focusRing}`}`}
                        />
                      ) : field.type === 'select' ? (
                        <select
                          value={form[field.key] || ''}
                          onChange={e => set(field.key, e.target.value)}
                          className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white ${formErrors[field.key] ? 'border-red-300 focus:ring-red-200' : `border-gray-200 ${theme.focusRing}`}`}
                        >
                          <option value="">{tr ? 'Seçin...' : 'Select...'}</option>
                          {field.options.map(opt => (
                            <option key={opt.value} value={opt.value}>{tr ? opt.labelTr : opt.labelEn}</option>
                          ))}
                        </select>

                      ) : field.type === 'toggle2' ? (
                        /* ── 2-option toggle ── */
                        <div className={`grid grid-cols-2 gap-2 ${formErrors[field.key] ? 'ring-2 ring-red-200 rounded-2xl p-1' : ''}`}>
                          {field.options.map(opt => {
                            const active = form[field.key] === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => set(field.key, opt.value)}
                                className={`text-left px-4 py-3 rounded-xl border-2 transition-all ${
                                  active
                                    ? `${theme.selectedBorder} ${theme.selectedBg} ${theme.selectedText}`
                                    : `border-gray-200 ${theme.hoverBorderSoft} text-gray-600`
                                }`}
                              >
                                <div className="text-sm font-semibold">{tr ? opt.labelTr : opt.labelEn}</div>
                                <div className="text-xs text-gray-400 mt-0.5">{tr ? opt.descTr : opt.descEn}</div>
                              </button>
                            );
                          })}
                        </div>

                      ) : field.type === 'number_unit' || field.type === 'area_smart' ? (
                        /* ── Number + unit (veya modüler chip seçici) ── */
                        (field.type === 'area_smart' && form.stand_design_type === 'modular') ? (
                          /* Modüler: chip butonlar */
                          <div className={`flex flex-wrap gap-2 ${formErrors[field.key] ? 'ring-2 ring-red-200 rounded-xl p-1' : ''}`}>
                            {modularAreas.map(area => {
                              const active = String(form[field.key]) === String(area);
                              return (
                                <button
                                  key={area}
                                  type="button"
                                  onClick={() => set(field.key, String(area))}
                                  className={`px-5 py-2.5 rounded-xl border-2 font-semibold text-sm transition-all ${
                                    active
                                      ? `${theme.selectedBorder} ${theme.chipActiveBg} text-white shadow-sm ${theme.chipActiveShadow}`
                                      : `border-gray-200 bg-white text-gray-700 ${theme.chipHover}`
                                  }`}
                                >
                                  {area} m²
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          /* Custom veya belirsiz: serbest sayı girişi */
                          <div className="flex gap-0 items-stretch">
                            <input
                              type="number"
                              min="1"
                              max="9999"
                              value={form[field.key] || ''}
                              onChange={e => set(field.key, e.target.value.replace(/\D/g, ''))}
                              placeholder={field.placeholder}
                              className={`flex-1 border border-r-0 rounded-l-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${formErrors[field.key] ? 'border-red-300 focus:ring-red-200' : `border-gray-200 ${theme.focusRing}`}`}
                            />
                            <span className="bg-gray-50 border border-gray-200 rounded-r-xl px-4 flex items-center text-sm font-semibold text-gray-500">
                              {field.unit}
                            </span>
                          </div>
                        )

                      ) : field.type === 'card_radio' ? (
                        /* ── Card radio (stand shape) ── */
                        <div className={`grid grid-cols-4 gap-2 ${formErrors[field.key] ? 'ring-2 ring-red-200 rounded-xl p-1' : ''}`}>
                          {field.options.map(opt => {
                            const active = form[field.key] === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => set(field.key, opt.value)}
                                className={`flex flex-col items-center text-center px-2 py-3 rounded-xl border-2 transition-all ${
                                  active
                                    ? `${theme.selectedBorder} ${theme.selectedBg} ${theme.selectedText}`
                                    : `border-gray-200 ${theme.hoverBorderSoft} text-gray-500`
                                }`}
                              >
                                <span className="text-lg mb-1">
                                  {opt.value === 'island' ? '⬜' : opt.value === 'peninsula' ? '▭' : opt.value === 'linear' ? '▬' : '◳'}
                                </span>
                                <div className="text-xs font-semibold leading-tight">{tr ? opt.labelTr : opt.labelEn}</div>
                                <div className="text-[10px] text-gray-400 mt-0.5">{tr ? opt.descTr : opt.descEn}</div>
                              </button>
                            );
                          })}
                        </div>

                      ) : field.type === 'multi_check' ? (
                        /* ── Multi-checkbox ── */
                        <div className={`grid grid-cols-2 gap-2 ${formErrors[field.key] ? 'ring-2 ring-red-200 rounded-xl p-1' : ''}`}>
                          {field.options.map(opt => {
                            const selected = (form[field.key] || []).includes(opt.value);
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => {
                                  const cur = form[field.key] || [];
                                  set(field.key, selected
                                    ? cur.filter(v => v !== opt.value)
                                    : [...cur, opt.value]);
                                }}
                                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm transition-all ${
                                  selected
                                    ? `${theme.selectedBorder} ${theme.selectedBg} ${theme.selectedText} font-semibold`
                                    : `border-gray-200 ${theme.hoverBorderSoft} text-gray-600`
                                }`}
                              >
                                <div className={`h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                  selected ? `${theme.selectedBorder} ${theme.checkboxActiveBg}` : 'border-gray-300'
                                }`}>
                                  {selected && <Check className="h-2.5 w-2.5 text-white" />}
                                </div>
                                {tr ? opt.labelTr : opt.labelEn}
                              </button>
                            );
                          })}
                        </div>

                      ) : (
                        <input
                          type={field.type === 'url' ? 'text' : field.type}
                          value={form[field.key] || ''}
                          onChange={e => {
                            set(field.key, e.target.value);
                            // URL alanında hata varsa yazarken temizle
                            if (field.type === 'url' && formErrors[field.key]) {
                              setFormErrors(prev => { const n = {...prev}; delete n[field.key]; return n; });
                            }
                          }}
                          onBlur={() => {
                            if (field.type === 'url') {
                              const val = String(form[field.key] || '').trim();
                              if (val && !isValidUrl(val)) {
                                setFormErrors(prev => ({
                                  ...prev,
                                  [field.key]: tr ? 'Geçersiz web adresi. Örnek: www.xxx.com' : 'Invalid website. Example: www.xxx.com'
                                }));
                              } else {
                                setFormErrors(prev => { const n = {...prev}; delete n[field.key]; return n; });
                              }
                            }
                          }}
                          placeholder={field.placeholder}
                          className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${formErrors[field.key] ? 'border-red-300 focus:ring-red-200' : `border-gray-200 ${theme.focusRing}`}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer buttons */}
              <div className="px-8 pb-6 flex items-center justify-between gap-3">
                {stepIndex >= 1 ? (
                  <button
                    onClick={goBack}
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 transition-colors px-3 py-2 rounded-xl hover:bg-gray-50"
                  >
                    <ChevronLeft className="h-4 w-4" />{tr ? 'Geri' : 'Back'}
                  </button>
                ) : (
                  <div />
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={goNext}
                    disabled={saving}
                    className={`flex items-center gap-2 ${theme.primaryBtn} text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition-all disabled:opacity-60`}
                  >
                    {saving
                      ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      : isLast ? (tr ? 'Tamamla' : 'Finish') : (tr ? 'İleri' : 'Next')}
                    {!saving && !isLast && <ChevronRight className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Leave confirmation modal ── */}
      {showLeaveModal && (
        <div
          className="fixed inset-0 z-[10000] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="wizard-leave-modal"
        >
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xl">⚠️</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {tr ? 'Emin misiniz?' : 'Are you sure?'}
                </h3>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                  {tr
                    ? 'Henüz "Tamamla" butonuna basmadınız. Şimdi kapatırsanız bu adımdaki ilerlemeniz kaydedilmeyebilir ve daha sonra baştan başlamanız gerekebilir.'
                    : 'You have not clicked "Finish" yet. If you close now, your progress in this step may not be saved and you might need to start over.'}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowLeaveModal(false)}
                data-testid="wizard-leave-cancel-btn"
                className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
              >
                {tr ? 'Devam Et' : 'Keep Editing'}
              </button>
              <button
                type="button"
                onClick={confirmLeave}
                data-testid="wizard-leave-confirm-btn"
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors"
              >
                {tr ? 'Yine de Çık' : 'Leave Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Idle / Inactivity timeout modal ── */}
      {showIdleModal && (
        <div
          className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="wizard-idle-modal"
        >
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 text-center">
            <div className="h-14 w-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⏱️</span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {tr ? 'Oturum Zaman Aşımı' : 'Session Timeout'}
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-5">
              {tr
                ? 'Uzun süre hareketsiz kaldığınız için oturumunuz zaman aşımına uğradı. Lütfen onboarding sürecini baştan başlatınız.'
                : 'Your session has timed out due to inactivity. Please restart the onboarding process from the beginning.'}
            </p>
            <button
              type="button"
              onClick={handleIdleTimeout}
              data-testid="wizard-idle-ok-btn"
              className={`w-full ${theme.primaryBtn} text-white font-semibold py-2.5 rounded-xl text-sm transition-all`}
            >
              {tr ? 'Tamam' : 'OK'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingWizard;
